import { generateUuid } from '@witmetrics/utils/uuid';
import { formatPrice, isBlank, strip } from '@witmetrics/utils/strings';
import type {
  Currency,
  DecoratedOrderNote,
  DecoratedUnisonProjectOrder,
  GroupedOrderProduct,
  Nullish,
} from '@witmetrics/utils';
import {
  DESCENDING,
  ORDER_PRODUCT_STATUS_ID,
  ORDER_STATUS_ID,
} from '@witmetrics/utils/constants';
import { isEmpty } from '@witmetrics/utils/misc';
import { ungroupOrderProducts } from '@witmetrics/utils/products';
import type { CreateNewOrderRequest } from '@witmetrics/api-client';
import { API } from '@/api';
import type { DraftOrderProduct } from '@/types/orders';

export const classes = {
  dialog:
    'w-[80vw] min-w-[400px] max-w-[1000px] min-h-[450px] max-h-[675px] h-[80vh] flex flex-col',
  body: 'grow pt-0',
  wrapper: 'flex justify-between h-full',
  form: 'flex flex-col w-[325px]',
  input: 'w-full mb-6',
  divider: 'mx-8 my-0',
  formWrapper: 'grow',
  message:
    'text-grey-7 dark:text-mauve-2 text-xl font-normal text-center py-8 px-4',
  list: 'my-4 min-h-0 max-h-[350px] overflow-y-auto',
  totalWrapper:
    'w-full flex justify-end px-8 py-4 text-base font-normal text-grey-7 dark:text-mauve-2',
  total: ' ml-4 font-medium text-grey-8 dark:text-mauve-1',
};

export async function fetchLabProducts(practiceId: number, labId: number) {
  return (
    await API.Practices.fetchPracticeLabProducts(practiceId, labId, {
      filters: [],
      pagination: { page: 1, pageSize: 250 }, // NOTE: Should be plenty big enough
    })
  ).results;
}

export function buildNewProduct(
  newProduct: DraftOrderProduct
): GroupedOrderProduct {
  return {
    uuid: generateUuid(),
    productId: newProduct.productId,
    quantity: newProduct.quantity,
    orderProductStatusId: ORDER_PRODUCT_STATUS_ID.PENDING,
    price: newProduct.price,
    idArray: [],
  };
}

export function validateFields(
  labId: number | Nullish,
  orderProducts: GroupedOrderProduct[]
) {
  let invalidFields = [];
  if (!labId) invalidFields.push('labId');
  if (isEmpty(orderProducts)) invalidFields.push('orderProducts');
  return invalidFields;
}
export function buildOrderData(
  labId: number,
  practiceId: number,
  externalRef: string,
  orderProducts: GroupedOrderProduct[]
): CreateNewOrderRequest {
  return {
    orderStatusId: ORDER_STATUS_ID.PENDING,
    labId,
    practiceId,
    externalRef: strip(externalRef, true),
    orderProducts: ungroupOrderProducts(orderProducts).map((op) => ({
      productId: op.productId,
      price: op.price,
      comments: '',
    })),
  };
}

export async function createNewOrder(
  data: CreateNewOrderRequest,
  projectId: number | Nullish,
  notes: string
) {
  let unisonProjectOrder: DecoratedUnisonProjectOrder | undefined;
  let orderNote: DecoratedOrderNote | undefined;
  const order = await API.Orders.createNewOrder(data);
  if (projectId) {
    unisonProjectOrder = await API.UnisonProjects.addOrderToUnisonProject(
      projectId,
      order.id
    );
  }
  if (!isBlank(notes)) {
    const note = await API.Notes.createNewNote({ content: notes });
    orderNote = await API.Orders.addNoteToOrder(order.id, note.id);
  }
  return { order, unisonProjectOrder, orderNote };
}

export async function fetchRecentProjects() {
  return (
    await API.UnisonProjects.fetchUnisonProjects({
      filters: [],
      pagination: { page: 1, pageSize: 10 },
      sorting: { sortBy: 'updatedAt', sortOrder: DESCENDING },
    })
  ).results;
}

export function sumTotal(
  orderProducts: GroupedOrderProduct[],
  currency?: Currency
) {
  return formatPrice(
    orderProducts.reduce((sum, p) => sum + p.quantity * parseFloat(p.price), 0),
    currency
  );
}
